<template>
  <div class="ol-swipe">
    <div class="map" id="olMap">
      <transition enter-active-class="slide-in-right" leave-active-class="slide-out-right">
        <div class="ol-map-legends" v-show="legends.length > 0">
          <!-- <div class="ol-map-legend-unit">{{ unit }}</div> -->

          <div class="ol-map-legend" v-for="item in legends" :key="item.id">
            <div class="ol-map-legend-icon" :style="{ backgroundColor: item.color }"></div>
            <div class="ol-map-legend-label">{{ item.labelText }}</div>
          </div>
        </div>
      </transition>
    </div>
    <el-slider v-model="slider" :show-tooltip="false" @input="onInput"></el-slider>
  </div>
</template>

<script>
import Map from 'ol/Map'
import View from 'ol/View'
import { defaults as defaultControls } from 'ol/control'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import XYZ from 'ol/source/XYZ'
import TileWMS from 'ol/source/TileWMS'
import VectorSource from 'ol/source/Vector'
import GeoJSON from 'ol/format/GeoJSON'
import parseStyle from '../utils/style'
import { getVectorContext, getRenderPixel } from 'ol/render'
import { getTreeItem } from '@/utils/tools'
import { getBoundary, getProductResult } from '@/api/common'

export default {
  name: 'OlSwipe',

  props: {
    unit: {
      type: String,
      default: ''
    },
    extent: {
      type: Array||String,
      default: ''
    },
    legends: {
      type: Array,
      required: true
    },
    projectValue: {
      type: String,
      default: ''
    },
    timeline: {
      type: Object,
      require: true
    }
  },

  data () {
    return {
      slider: 50,
      map: null
    }
  },

  async mounted () {
    this.$waiting.show()
    const regions = JSON.parse(localStorage.getItem('regions'))
    let query = localStorage.getItem("query");
    if (query) {
      query = JSON.parse(query);
    }
    const regionId = query.regionId
    const { lon, lat, vertexBbox } = getTreeItem(regions, regionId)
    const extent = vertexBbox.slice(4, -1).split(',').map(item => item.split(' ')).reduce((pre, cur) => pre.concat(cur)).map(item => item * 1)
    const view = new View({
      projection: 'EPSG:4326',
      center: [lon, lat],
      padding: [20, 20, 20, 20],
      maxZoom: 17,
      minZoom: 8
    })

    const imgMapMark = new TileLayer({
      name: '底图标注',
      source: new XYZ({
        url: 'http://t{0-7}.tianditu.gov.cn/cia_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cia&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=95b976bf1c852b261315ac5bcd2c3955'
      })
    })

    const imgMap = new TileLayer({
      name: '影像底图',
      source: new XYZ({
        url: 'http://t0.tianditu.gov.cn/img_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=img&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=95b976bf1c852b261315ac5bcd2c3955'
      })
    })

    const { data: boundary } = await getBoundary(regionId)
    const features = new GeoJSON().readFeatures(boundary)
    const clipLayer = new VectorLayer({
      style: null,
      source: new VectorSource({ features })
    })

    const { layer: layerName, hdLayer: hdLayerName } = await getProductResult(this.timeline)

    const tileLayer = new TileLayer({
      className: 'stamen',
      name: '成果图层',
      source: new TileWMS({
        url: process.env.VUE_APP_WMS,
        params: {
          layers: layerName,
          VERSION: '1.1.0',
          FORMAT: 'image/png',
           CQL_FILTER: `project_id=${this.projectValue} and data_date=${this.timeline}`
        }
      })
    })

    clipLayer.getSource().on('addfeature', () => tileLayer.setExtent(clipLayer.getSource().getExtent()))

    tileLayer.on('postrender', e => {
      const vectorContext = getVectorContext(e)
      e.context.globalCompositeOperation = 'destination-in'
      clipLayer.getSource().forEachFeature(feature => {
        vectorContext.drawFeature(feature, parseStyle({ fill: 'black' }))
      })
      e.context.globalCompositeOperation = 'source-over'
    })

    tileLayer.on('prerender', (event) => {
      const ctx = event.context
      const mapSize = this.map.getSize()
      const width = mapSize[0] * (this.slider / 100)
      const tl = getRenderPixel(event, [width, 0])
      const tr = getRenderPixel(event, [mapSize[0], 0])
      const bl = getRenderPixel(event, [width, mapSize[1]])
      const br = getRenderPixel(event, mapSize)

      ctx.save()
      ctx.beginPath()
      ctx.moveTo(tl[0], tl[1])
      ctx.lineTo(bl[0], bl[1])
      ctx.lineTo(br[0], br[1])
      ctx.lineTo(tr[0], tr[1])
      ctx.closePath()
      ctx.clip()
    })

    tileLayer.on('postrender', e => {
      const ctx = e.context
      ctx.restore()
    })

    const hdLayer = new TileLayer({
      className: 'stamen',
      name: '高清底图',
      source: new TileWMS({
        url: process.env.VUE_APP_WMS,
        params: {
          layers: hdLayerName,
          VERSION: '1.1.0',
          FORMAT: 'image/png'
        }
      })
    })

    clipLayer.getSource().on('addfeature', () => hdLayer.setExtent(clipLayer.getSource().getExtent()))

    hdLayer.on('postrender', e => {
      const vectorContext = getVectorContext(e)
      e.context.globalCompositeOperation = 'destination-in'
      clipLayer.getSource().forEachFeature(feature => {
        vectorContext.drawFeature(feature, parseStyle({ fill: 'black' }))
      })
      e.context.globalCompositeOperation = 'source-over'
    })

    const borderLayer = new TileLayer({
      name: '边界图层',
      source: new TileWMS({
        url: process.env.VUE_APP_WMS,
        params: {
          layers: 'henan:high_field_land',
          VERSION: '1.1.0',
          FORMAT: 'image/png',
          STYLES: "henan:land_style",
          CQL_FILTER: `region_id=${regionId} or parent_id=${regionId}`
        }
      }),
      zIndex: 4
    })

    this.map = new Map({
      controls: defaultControls({
        zoom: false
      }),
      layers: [imgMap, hdLayer, tileLayer, imgMapMark, borderLayer],
      target: 'olMap',
      view
    })

    view.fit(this.extent, { padding: [80, 20, 80, 20] })
    this.$waiting.hidden()
  },

  methods: {
    onInput () {
      if (this.map) {
        this.map.render()
      }
    }
  },

  beforeDestroy () {
    this.map = null
  }
}
</script>

<style lang="scss" scoped>
.ol-swipe {
  display: flex;
  flex-direction: column;
  height: 100%;

  .map {
    position: relative;
    flex: 1;
    margin-bottom: 10px;
  }

  .ol-map-legends {
    position: absolute;
    right: 10px;
    bottom: 10px;
    display: flex;
    align-items: center;
    // min-width: 400px;
    height: 24px;
    font-size: 12px;
    padding: 0 10px;
    border: 1px solid rgba(30, 124, 104, 1);
    border-radius: 4px;
    backdrop-filter: blur(4px);
    background-color: rgba(4, 54, 35, 0.8);
    box-shadow: 0px 4px 10px 0px rgba(146, 159, 168, 0.4);
    z-index: 2;
  }

  .ol-map-legend-unit {
    flex: 1;
    text-align: center;
    color: #fff;
  }

  .ol-map-legend-all {
    height: 18px;
    font-size: 14px;
    color: #fff;
    margin-left: 10px;
    padding: 0 5px;
    border-radius: 2px;
    backdrop-filter: blur(4px);
    background-color: rgba(0, 125, 220, .8);
    cursor: pointer;
  }

  .ol-map-legend-toggle {
    margin-right: 0;
  }

  .ol-map-legend {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: 10px;
    // cursor: pointer;
  }

  .ol-map-legend-icon {
    width: 10px;
    height: 10px;
    margin-right: 4px;
    border-radius: 1px;
  }

  .ol-map-legend-label {
    color: #fff;
  }
}
</style>
